import React, { useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { listDevices, type ApiVendoDevice } from '@/api/vendo';
import {
   listTeamApi,
   inviteTeamApi,
   updateTeamApi,
   removeTeamApi,
   listTeamActivityApi,
   TEAM_PAGES,
   TEAM_SWITCHES,
   NO_SWITCHES,
   type TeamMemberRow,
   type TeamAccess,
   type TeamPage,
   type TeamSwitches,
} from '@/api/team';
import { relativeTime, exactTime } from '@/utils/relativeTime';
import { describeTeamActivity } from '@/utils/teamActivity';
import { useTeamStore, actingHasPage } from '@/store/useTeamStore';

/**
 * /wifi-team — Team access (Ray 2026-10-04, second design 2026-10-05). Share
 * your WHOLE account with someone who uses THEIR OWN SmartFi login: they pick
 * your email under "Switch to" in their profile menu. You choose which menu
 * pages they get and three switches. Your account settings always stay
 * yours. Everything a helper changes is listed under Activity.
 */

type FormState = {
   email: string;
   access: TeamAccess;
   pages: TeamPage[];
   switches: TeamSwitches;
};

/** A new share opens on the SAFE side: choose, nothing ticked, switches off. */
export const NEW_SHARE: FormState = { email: '', access: 'custom', pages: [], switches: { ...NO_SWITCHES } };

type Tab = 'invite' | 'team' | 'activity';

/** Share my account — one panel with three tabs (Ray 2026-10-15: no page, no
 *  stacked modals). Used inside the header Share dialog and, for old
 *  bookmarks, on the /wifi-team route. */
const ShareBody: React.FC<{ initialTab?: Tab }> = ({ initialTab = 'invite' }) => {
   const queryClient = useQueryClient();
   const acting = useTeamStore((s) => s.acting);
   const { data: teamData, isLoading } = useQuery({ queryKey: ['team'], queryFn: listTeamApi });
   const { data: devicesData } = useQuery({ queryKey: ['team-devices'], queryFn: () => listDevices() });
   const devices: ApiVendoDevice[] = devicesData?.devices ?? [];
   const nameById = useMemo(() => new Map(devices.map((d) => [d._id, d.name])), [devices]);

   const [tab, setTab] = useState<Tab>(initialTab);
   const freshInvite = (): FormState => ({ ...NEW_SHARE, switches: { ...NO_SWITCHES } });
   const [form, setForm] = useState<{ mode: 'invite' | 'edit'; id?: string; value: FormState }>({
      mode: 'invite',
      value: freshInvite(),
   });
   const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
   const [busy, setBusy] = useState(false);

   const refresh = () => queryClient.invalidateQueries({ queryKey: ['team'] });
   const members = teamData?.members ?? [];
   const canShare = actingHasPage(acting, 'wifi-team');

   const submit = async () => {
      if (busy) return;
      const v = form.value;
      if (form.mode === 'invite' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) {
         toast.error('Type their email, e.g. juan@gmail.com');
         return;
      }
      setBusy(true);
      try {
         const body = {
            access: v.access,
            pages: v.access === 'custom' ? v.pages : [],
            switches: v.access === 'custom' ? v.switches : { ...NO_SWITCHES },
         };
         if (form.mode === 'invite') {
            const r = await inviteTeamApi({ email: v.email.trim(), ...body });
            toast.success(
               r.emailed
                  ? `Invite sent to ${v.email.trim()}`
                  : `Invite saved, but the email did not send. Ask ${v.email.trim()} to try again later.`
            );
         } else {
            await updateTeamApi(form.id!, body);
            toast.success('Access updated');
         }
         setForm({ mode: 'invite', value: freshInvite() });
         await refresh();
         setTab('team');
      } catch (err: any) {
         toast.error(err?.message || 'Could not save');
      } finally {
         setBusy(false);
      }
   };

   const remove = async (m: TeamMemberRow) => {
      if (busy) return;
      setBusy(true);
      try {
         await removeTeamApi(m._id);
         toast.success(`${m.memberName || m.email} can no longer help`);
         setConfirmRemove(null);
         await refresh();
      } catch (err: any) {
         toast.error(err?.message || 'Could not remove');
      } finally {
         setBusy(false);
      }
   };

   const startEdit = (m: TeamMemberRow) => {
      setForm({
         mode: 'edit',
         id: m._id,
         value: {
            email: m.email,
            access: m.access,
            pages: m.pages ?? [],
            switches: { ...NO_SWITCHES, ...(m.switches ?? {}) },
         },
      });
      setTab('invite');
   };

   return (
      <Body>
         <Tabs role="tablist">
            <TabBtn type="button" role="tab" aria-selected={tab === 'invite'} $on={tab === 'invite'} onClick={() => setTab('invite')}>
               {form.mode === 'edit' ? 'Change access' : 'New invite'}
            </TabBtn>
            <TabBtn type="button" role="tab" aria-selected={tab === 'team'} $on={tab === 'team'} onClick={() => setTab('team')}>
               Team{members.length ? ` (${members.length})` : ''}
            </TabBtn>
            <TabBtn type="button" role="tab" aria-selected={tab === 'activity'} $on={tab === 'activity'} onClick={() => setTab('activity')}>
               Activity
            </TabBtn>
         </Tabs>

         {tab === 'invite' && (
            <>
               {form.mode === 'edit' && (
                  <Muted>
                     Changing access for <b>{form.value.email}</b>.{' '}
                     <LinkBtn type="button" onClick={() => setForm({ mode: 'invite', value: freshInvite() })}>
                        Start a new invite instead
                     </LinkBtn>
                  </Muted>
               )}
               <AccessForm
                  value={form.value}
                  onChange={(value) => setForm({ ...form, value })}
                  emailEditable={form.mode === 'invite'}
               />
               <ModalActions>
                  <PrimaryBtn type="button" onClick={submit} disabled={busy || !canShare}>
                     {busy ? 'Saving…' : form.mode === 'invite' ? 'Send invite' : 'Save'}
                  </PrimaryBtn>
               </ModalActions>
            </>
         )}

         {tab === 'team' && (
            <>
               {isLoading && <SkeletonLines lines={3} />}
               {!isLoading && members.length === 0 && (
                  <Empty>
                     <div>Nobody is helping you yet.</div>
                     <PrimaryBtn type="button" onClick={() => setTab('invite')} disabled={!canShare}>
                        Send an invite
                     </PrimaryBtn>
                  </Empty>
               )}
               {members.map((m) => (
                  <Row key={m._id}>
                     <RowMain>
                        <RowName>
                           {m.memberName || m.email}
                           {m.source === 'seller-auto' && (
                              <Pill title="Added automatically because this person sold you a vendo. Remove it anytime.">
                                 Seller (automatic)
                              </Pill>
                           )}
                        </RowName>
                        <RowMeta>
                           {m.memberName ? `${m.email} · ` : ''}
                           {m.accessSummary}
                           {m.status === 'pending' ? (
                              <Pill title={exactTime(m.invitedAt)}>Invited {relativeTime(m.invitedAt)}</Pill>
                           ) : (
                              <Pill $ok title={exactTime(m.acceptedAt)}>Joined {relativeTime(m.acceptedAt)}</Pill>
                           )}
                        </RowMeta>
                     </RowMain>
                     {confirmRemove === m._id ? (
                        <>
                           <Muted>Remove?</Muted>
                           <DangerBtn type="button" onClick={() => remove(m)} disabled={busy}>
                              {busy ? 'Removing…' : 'Yes, remove'}
                           </DangerBtn>
                           <GhostBtn type="button" onClick={() => setConfirmRemove(null)}>
                              Keep
                           </GhostBtn>
                        </>
                     ) : (
                        <>
                           <GhostBtn type="button" onClick={() => startEdit(m)}>
                              Change access
                           </GhostBtn>
                           <GhostBtn type="button" onClick={() => setConfirmRemove(m._id)}>
                              Remove
                           </GhostBtn>
                        </>
                     )}
                  </Row>
               ))}
            </>
         )}

         {tab === 'activity' && <ActivityCard nameById={nameById} plain />}
      </Body>
   );
};

/** The /wifi-team route (old bookmarks): same panel, on a page. */
const Team: React.FC = () => (
   <Page>
      <Head>
         <div>
            <Title>Share my account</Title>
            <Sub>
               Share your account with someone who uses their own SmartFi login — no
               password sharing. They open it from "Switch to" in their profile menu.
               Your account settings always stay yours.
            </Sub>
         </div>
      </Head>
      <Card>
         <ShareBody initialTab="team" />
      </Card>
   </Page>
);

export default Team;

/** Top-bar "Share" dialog — opens straight on the invite form. */
export const ShareDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => (
   <Overlay>
      <Dialog role="dialog" aria-label="Share my account" $wide>
         <DialogHead>
            <DialogTitle>Share my account</DialogTitle>
            <CloseX type="button" aria-label="Close" onClick={onClose}>
               ×
            </CloseX>
         </DialogHead>
         <ShareBody />
      </Dialog>
   </Overlay>
);

/* ── access form ─────────────────────────────────────────────────── */

export const AccessForm: React.FC<{
   value: FormState;
   onChange: (v: FormState) => void;
   emailEditable: boolean;
}> = ({ value, onChange, emailEditable }) => {
   const togglePage = (k: TeamPage) =>
      onChange({
         ...value,
         pages: value.pages.includes(k) ? value.pages.filter((x) => x !== k) : [...value.pages, k],
      });
   return (
      <FormBody>
         {emailEditable && (
            <Field>
               <Lbl htmlFor="team-email">Their email</Lbl>
               <Input
                  id="team-email"
                  type="email"
                  placeholder="e.g. juan@gmail.com"
                  value={value.email}
                  onChange={(e) => onChange({ ...value, email: e.target.value })}
               />
               <Hint>They sign in with their own SmartFi account. No account yet? The email lets them make one.</Hint>
            </Field>
         )}
         <Field>
            <Lbl as="span">What can they use?</Lbl>
            <Choices role="radiogroup" aria-label="What can they use?">
               <ChoiceCard $on={value.access === 'full'}>
                  <input
                     type="radio"
                     name="team-access"
                     checked={value.access === 'full'}
                     onChange={() => onChange({ ...value, access: 'full' })}
                  />
                  <span>
                     <ChoiceTitle>Full access</ChoiceTitle>
                     <ChoiceSub>Every page, transfer credits, delete a vendo, and money amounts.</ChoiceSub>
                  </span>
               </ChoiceCard>
               <ChoiceCard $on={value.access === 'custom'}>
                  <input
                     type="radio"
                     name="team-access"
                     checked={value.access === 'custom'}
                     onChange={() => onChange({ ...value, access: 'custom' })}
                  />
                  <span>
                     <ChoiceTitle>Choose what they can do</ChoiceTitle>
                     <ChoiceSub>Tick the pages they get. Unticked pages are hidden from them.</ChoiceSub>
                  </span>
               </ChoiceCard>
            </Choices>
            {value.access === 'custom' && (
               <>
                  <CheckList aria-label="Pages">
                     {TEAM_PAGES.map((p) => (
                        <Check key={p.key}>
                           <input type="checkbox" checked={value.pages.includes(p.key)} onChange={() => togglePage(p.key)} />
                           {p.label}
                        </Check>
                     ))}
                     {value.pages.length === 0 && <Muted>Nothing ticked yet — tick at least one page.</Muted>}
                  </CheckList>
                  <CheckList aria-label="Also allow" $cols={3}>
                     {TEAM_SWITCHES.map((sw) => (
                        <Check key={sw.key}>
                           <input
                              type="checkbox"
                              role="switch"
                              checked={value.switches[sw.key]}
                              onChange={() =>
                                 onChange({ ...value, switches: { ...value.switches, [sw.key]: !value.switches[sw.key] } })
                              }
                           />
                           {sw.label}
                        </Check>
                     ))}
                  </CheckList>
               </>
            )}
            <Hint>Your email, password and profile are never shared, and they cannot buy credits for you.</Hint>
         </Field>
      </FormBody>
   );
};

/* ── activity ────────────────────────────────────────────────────── */

const PER_PAGE = 20;

const ActivityCard: React.FC<{ nameById: Map<string, string>; plain?: boolean }> = ({ nameById, plain = false }) => {
   const [q, setQ] = useState('');
   const [page, setPage] = useState(1);
   const { data } = useQuery({
      queryKey: ['team-activity', page, q],
      queryFn: () => listTeamActivityApi({ page, limit: PER_PAGE, q: q.trim() || undefined }),
   });
   const rows = data?.rows ?? [];
   const pages = Math.max(1, Math.ceil((data?.total ?? 0) / PER_PAGE));
   return (
      <Card as={plain ? 'div' : undefined} style={plain ? { border: 0, padding: 0 } : undefined}>
         <CardHead>
            {!plain && <CardTitle>Activity</CardTitle>}
            <Input
               aria-label="Search activity"
               placeholder="Search name, email or change…"
               value={q}
               onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
               }}
               style={{ flex: 1, minWidth: 200 }}
            />
         </CardHead>
         {rows.length === 0 && <Muted>Nothing yet. Changes your helpers make show here.</Muted>}
         {rows.map((r) => (
            <Row key={r._id}>
               <RowMain>
                  <RowName>
                     {describeTeamActivity(r.method, r.path, (id) => nameById.get(id))}
                     {r.status >= 400 && <Pill>Not allowed</Pill>}
                  </RowName>
                  <RowMeta>
                     {r.actorName || r.actorEmail} · <span title={exactTime(r.at)}>{relativeTime(r.at)}</span>
                  </RowMeta>
               </RowMain>
            </Row>
         ))}
         {pages > 1 && (
            <Pager>
               <GhostBtn type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Prev
               </GhostBtn>
               <span>
                  Page {page} of {pages}
               </span>
               <GhostBtn type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                  Next
               </GhostBtn>
            </Pager>
         )}
      </Card>
   );
};

/* ── modal: never closes on an outside click (form safety) ───────── */

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
   title,
   onClose,
   children,
}) => (
   <Overlay>
      <Dialog role="dialog" aria-label={title}>
         <DialogHead>
            <DialogTitle>{title}</DialogTitle>
            <CloseX type="button" aria-label="Close" onClick={onClose}>
               ×
            </CloseX>
         </DialogHead>
         {children}
      </Dialog>
   </Overlay>
);

/* ── styles ──────────────────────────────────────────────────────── */

const Body = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
   /* Same height on every tab so the box does not jump when switching. */
   min-height: 420px;
   @media (max-width: 768px) {
      min-height: 0;
      flex: 1;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
   }
`;
const Empty = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 10px;
   text-align: center;
   color: ${colors.GRAY3};
   padding: 24px 0;
`;
const Tabs = styled.div`
   display: flex;
   gap: 4px;
   border-bottom: 1px solid ${colors.GRAY13};
   @media (max-width: 768px) {
      position: sticky;
      top: 0;
      background: ${colors.WHITE};
      z-index: 1;
   }
`;
const TabBtn = styled.button<{ $on: boolean }>`
   background: none;
   border: 0;
   border-bottom: 2px solid ${(p) => (p.$on ? colors.PRIMARY : 'transparent')};
   color: ${(p) => (p.$on ? colors.PRIMARY : colors.GRAY3)};
   font-weight: 600;
   font-size: 14px;
   padding: 8px 12px;
   margin-bottom: -1px;
   cursor: pointer;
`;
const LinkBtn = styled.button`
   background: none;
   border: 0;
   padding: 0;
   color: ${colors.PRIMARY};
   font: inherit;
   cursor: pointer;
   text-decoration: underline;
`;
const Page = styled.div`
   padding: 1rem;
   display: flex;
   flex-direction: column;
   gap: 16px;
   @media (max-width: 768px) {
      padding: 12px 0;
   }
`;
const Head = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: flex-start;
   gap: 12px;
   flex-wrap: wrap;
`;
const Title = styled.h2`
   margin: 0;
   font-size: 20px;
`;
const Sub = styled.p`
   margin: 4px 0 0;
   font-size: 13px;
   color: ${colors.GRAY3};
   max-width: 560px;
`;
const Card = styled.div`
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
   padding: 16px;
`;
const CardHead = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 12px;
   flex-wrap: wrap;
   margin-bottom: 8px;
`;
const CardTitle = styled.h3`
   margin: 0 0 8px;
   font-size: 15px;
`;
const Row = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 10px 0;
   border-top: 1px solid ${colors.GRAY13};
   &:first-of-type {
      border-top: none;
   }
`;
const RowMain = styled.div`
   flex: 1;
   min-width: 0;
`;
const RowName = styled.div`
   font-weight: 600;
   font-size: 14px;
   display: flex;
   gap: 8px;
   align-items: center;
   flex-wrap: wrap;
`;
const RowMeta = styled.div`
   font-size: 12px;
   color: ${colors.GRAY3};
   display: flex;
   gap: 6px;
   align-items: center;
   flex-wrap: wrap;
`;
const Pill = styled.span<{ $ok?: boolean }>`
   font-size: 11px;
   padding: 2px 8px;
   border-radius: 999px;
   background: ${({ $ok }) => ($ok ? '#e6f6ec' : '#fff1e0')};
   color: ${({ $ok }) => ($ok ? '#1d7a43' : '#8a5300')};
   font-weight: 600;
`;
const Muted = styled.p`
   margin: 4px 0;
   font-size: 13px;
   color: ${colors.GRAY3};
`;
const PrimaryBtn = styled.button`
   padding: 9px 16px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-weight: 600;
   font-size: 14px;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;
const GhostBtn = styled.button`
   padding: 7px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;
const DangerBtn = styled(PrimaryBtn)`
   background: #d64545;
`;
const Pager = styled.div`
   display: flex;
   gap: 10px;
   align-items: center;
   justify-content: flex-end;
   margin-top: 10px;
   font-size: 13px;
`;
const Overlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.45);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 1000;
   padding: 16px;
   /* Phone: a sheet that slides up from the bottom (Ray 2026-10-15). */
   @media (max-width: 768px) {
      align-items: flex-end;
      padding: 0;
   }
`;
const slideUp = keyframes`
   from { transform: translateY(24px); opacity: 0.6; }
   to { transform: translateY(0); opacity: 1; }
`;
const Dialog = styled.div<{ $wide?: boolean }>`
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
   border-radius: 8px; /* standard window corners (Claim a transfer) */
   padding: 18px;
   width: 100%;
   max-width: ${(p) => (p.$wide ? '760px' : '560px')};
   max-height: 90vh;
   overflow: auto;
   @media (max-width: 768px) {
      max-width: none;
      height: 88vh;
      max-height: 88vh;
      border-radius: 18px 18px 0 0;
      padding: 14px 14px calc(14px + env(safe-area-inset-bottom, 0px));
      display: flex;
      flex-direction: column;
      overflow: hidden;
      animation: ${slideUp} 0.22s ease-out;
      @media (prefers-reduced-motion: reduce) { animation: none; }
   }
`;
const DialogHead = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 10px;
`;
const DialogTitle = styled.h3`
   margin: 0;
   font-size: 16px;
`;
const CloseX = styled.button`
   border: none;
   background: none;
   font-size: 22px;
   cursor: pointer;
   line-height: 1;
`;
const ModalActions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 14px;
`;
const FormBody = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
`;
const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;
const Lbl = styled.label`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY3};
`;
const Input = styled.input`
   padding: 9px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 14px;
   width: 100%;
`;
const Hint = styled.span`
   font-size: 12px;
   color: ${colors.GRAY3};
`;
/* Two columns (three for the short switch list) — never one long column
   with the right half empty (Ray 2026-10-18). */
const CheckList = styled.div<{ $cols?: number }>`
   display: grid;
   grid-template-columns: repeat(${({ $cols }) => $cols ?? 2}, minmax(0, 1fr));
   gap: 6px 12px;
   > p { grid-column: 1 / -1; margin: 0; }
   @media (max-width: 520px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
   }
   max-height: 220px;
   overflow: auto;
   padding: 8px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
`;
const Check = styled.label<{ $sub?: boolean }>`
   display: flex;
   gap: 8px;
   align-items: center;
   font-size: 13px;
   padding-left: ${({ $sub }) => ($sub ? '20px' : '0')};
`;
const Choices = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 8px;
   @media (max-width: 520px) {
      grid-template-columns: 1fr;
   }
`;
const ChoiceCard = styled.label<{ $on: boolean }>`
   display: flex;
   gap: 10px;
   align-items: flex-start;
   padding: 12px;
   border-radius: 8px;
   cursor: pointer;
   border: 1.5px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $on }) => ($on ? 'rgba(59, 130, 246, 0.06)' : 'transparent')};
   input {
      margin-top: 3px;
   }
`;
const ChoiceTitle = styled.span`
   display: block;
   font-size: 14px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;
const ChoiceSub = styled.span`
   display: block;
   font-size: 12px;
   color: ${colors.GRAY3};
   margin-top: 2px;
`;
